export const CHUNK_HEAL_PARAM = '__taiari_chunk_heal'
export const CHUNK_RELOAD_STORAGE_KEY = 'taisen-board:chunk-error-reload'
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000

export type ChunkRecoveryReason =
  | 'react-error-boundary'
  | 'vite-preload-error'
  | 'unhandledrejection'
  | 'resource-error'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk',
  'ChunkLoadError',
  'Expected a JavaScript module script',
  'disallowed MIME type',
  'error loading dynamically imported module',
  'Failed to load module script',
]

const STALE_ASSET_PATTERN = /(?:https?:\/\/[^\s"'<>]+)?\/assets\/[^\s"'<>]+\.(?:js|mjs|css)(?:[?#][^\s"'<>]*)?/gi

type ErrorLikeObject = {
  message?: unknown
  stack?: unknown
  src?: unknown
  href?: unknown
  filename?: unknown
}

export type ChunkErrorMatch = {
  matched: boolean
  detail: string
}

export type ChunkReloadDecisionInput = {
  href: string
  now: number
  previousReloadAt: number | null
  storageAvailable: boolean
  cooldownMs?: number
}

export type ChunkReloadDecision = {
  shouldReload: boolean
  nextHref: string | null
  markerPresent: boolean
  cooldownOk: boolean
}

function isObject(value: unknown): value is ErrorLikeObject {
  return typeof value === 'object' && value !== null
}

function stringifyValue(value: unknown): string {
  if (value instanceof Error) return value.message
  if (typeof value === 'string') return value
  if (isObject(value)) {
    const parts = [value.message, value.src, value.href, value.filename]
      .filter((part): part is string => typeof part === 'string')
    if (parts.length > 0) return parts.join('\n')
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function isSameOriginStaleAssetUrl(candidate: string, currentOrigin: string): boolean {
  try {
    const url = new URL(candidate, currentOrigin)
    if (url.origin !== currentOrigin) return false
    if (!url.pathname.startsWith('/assets/')) return false
    return /\.(js|mjs|css)$/.test(url.pathname)
  } catch {
    return false
  }
}

export function isStaleAssetUrl(value: string, currentOrigin: string): boolean {
  return isSameOriginStaleAssetUrl(value, currentOrigin)
}

export function matchRecoverableChunkError(value: unknown, currentOrigin: string): ChunkErrorMatch {
  const text = stringifyValue(value)
  if (!text) return { matched: false, detail: '' }

  for (const pattern of CHUNK_ERROR_PATTERNS) {
    if (text.includes(pattern)) return { matched: true, detail: pattern }
  }

  STALE_ASSET_PATTERN.lastIndex = 0
  for (const match of text.matchAll(STALE_ASSET_PATTERN)) {
    if (isSameOriginStaleAssetUrl(match[0], currentOrigin)) {
      return { matched: true, detail: match[0] }
    }
  }

  if (isSameOriginStaleAssetUrl(text, currentOrigin)) return { matched: true, detail: text }

  return { matched: false, detail: text }
}

export function isRecoverableChunkError(value: unknown, currentOrigin: string): boolean {
  return matchRecoverableChunkError(value, currentOrigin).matched
}

export function computeChunkReloadDecision(input: ChunkReloadDecisionInput): ChunkReloadDecision {
  const cooldownMs = input.cooldownMs ?? CHUNK_RELOAD_COOLDOWN_MS
  const url = new URL(input.href)
  const markerPresent = url.searchParams.has(CHUNK_HEAL_PARAM)
  const previous = input.previousReloadAt
  const cooldownOk =
    !input.storageAvailable ||
    previous === null ||
    !Number.isFinite(previous) ||
    input.now - previous >= cooldownMs

  if (markerPresent || !cooldownOk) {
    return { shouldReload: false, nextHref: null, markerPresent, cooldownOk }
  }

  url.searchParams.set(CHUNK_HEAL_PARAM, String(input.now))
  return { shouldReload: true, nextHref: url.toString(), markerPresent, cooldownOk }
}

export function clearChunkHealMarkerFromHref(href: string): string {
  const url = new URL(href)
  if (!url.searchParams.has(CHUNK_HEAL_PARAM)) return href
  url.searchParams.delete(CHUNK_HEAL_PARAM)
  return url.toString()
}
